import React, { useState } from 'react';
import { Checkbox, Input } from 'antd';
import { PushpinOutlined } from '@ant-design/icons';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import {
  AUTHOR_TAGS,
  AUTHORS,
  PODIUMAUTHORS,
  SEND_TO_CRM,
  SENT_TO_CRM_AO3,
  SENT_TO_CRM_RR,
  SOURCE,
  WATCHLIST
} from '../../../core/constants';
import { IBook } from '../../../section/Common/types/types';
import { useActions } from '../../../context/Actions.context';
import { useServices } from '../../../core/services/useServices';
import showActionNotification from '../../../section/Common/utils/showActionNotification';
import Endpoints from '../../../core/constants/endpoints';

const { TextArea } = Input;

interface IProps {
  book: IBook | Record<string, string | number>;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const watchlistObject = (source, id, book) => {
  if (source === SOURCE.bookstat) {
    return {
      obj: { amazonasin: book.ASIN, notes: '' },
      obj_type: 'Title'
    };
  } else if (source === SOURCE['royal-road']) {
    return (id === AUTHORS || id === PODIUMAUTHORS)
      ? { obj: { author_id: book['Author Id'], tag: 'watchlist', notes: '' }, obj_type: 'Author' }
      : { obj: { fiction_id: book['Fiction Id'], notes: '' }, obj_type: 'Title' };
  } else if (source === SOURCE.ao3) {
    return {
      obj: { work_id: book.work_id, notes: '', obj_type: 'Title' }
    };
  }
};

const WatchlistTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { source, isActionLoading, setActionLoading } = useActions();
  const { book: bookService } = useServices();
  const queryClient = useQueryClient();
  const history = useHistory();
  const watchlist = watchlistObject(source['source'], id, book);
  const objectType = watchlist?.obj_type ?? 'Title';
  const [notes, setNotes] = useState<string>('');
  const [pinOnSend, setPinOnSend] = useState<boolean>(false);

  const { mutateAsync: mutateWatchlist } = useUpdateTitle(WATCHLIST);
  const { mutateAsync: mutateAuthorWatchlist } = useUpdateTitle(AUTHOR_TAGS);

  const handleClose = () => {
    setNotes('');
    setPinOnSend(false);
    toggleModal();
  };

  const onSend = () => {
    setActionLoading(true);

    const body = {
      ...(notes.trim() && { notes: notes.trim() }),
      ...(pinOnSend && { is_pinned_watchlist: 1 })
    };

    const capturedSource = source['source'];
    const capturedParams = { ...watchlist?.obj };

    const promise = (id === AUTHORS || id === PODIUMAUTHORS)
      ? mutateAuthorWatchlist({ params: capturedParams, body })
      : mutateWatchlist({ params: capturedParams, body });

    promise.then(res => {
      if (res?.status === 200) {
        showActionNotification({
          type: 'success',
          message: `The ${objectType} was successfully added to watchlist${pinOnSend ? ' and pinned' : ''}.`,
          destination: {
            name: 'Watchlist',
            onGoTo: () => history.push(`${capturedSource}/watchlist`)
          },
          onUndo: async () => {
            await bookService.updateTitle(
              {
                source: capturedSource,
                sourceTab: 'watchlist',
                endpoint: Endpoints[id],
                action: id
              },
              capturedParams
            );
          },
          queryClient,
          id
        });
      } else {
        showActionNotification({
          type: 'error',
          message: `The ${objectType} has not been sent to watchlist.`
        });
      }
    }).catch(() => {
      showActionNotification({
        type: 'error',
        message: `The ${objectType} has not been sent to watchlist.`
      });
    });

    promise.then(() => {
      if (id === SEND_TO_CRM || id === SENT_TO_CRM_RR || id === SENT_TO_CRM_AO3) {
        showActionNotification({ type: 'info', message: 'Please delete the data from Hubspot.' });
      }
    });
    promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
    promise.then(() => queryClient.refetchQueries(`get-${id}`));
    promise.finally(() => setActionLoading(false));

    handleClose();
  };

  return (
    <Modal
      okText={'Send To Watchlist'}
      title='Send To Watchlist'
      visible={modal}
      onOk={onSend}
      confirmLoading={isActionLoading}
      onCancel={handleClose}
    >
      <div>
        <span>
          Help yourself and others understand why you&apos;re sending this. Leave a note, or skip and
          continue.
        </span>
        <br />
        <br />
        <TextArea
          placeholder='Add your notes here...'
          maxLength={400}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />
        <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
          {notes.length} / 400
        </div>

        {/* Pin checkbox — only available for Titles, not Authors */}
        {objectType === 'Title' && (
          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={pinOnSend}
              onChange={(e) => setPinOnSend(e.target.checked)}
            >
              <PushpinOutlined style={{ marginRight: 6 }} />
              Pin this title to the top of Watchlist
            </Checkbox>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default React.memo(WatchlistTitles);
